import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Badge, Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import { getCallsByRestaurant, acknowledgeCall } from "../../../services/CallsService";
import { fetchConnectedUser } from "../../../services/OrdersService";
import { toast } from "react-toastify";
import { Colors } from "../../../contants/Colors";
import { useTranslation } from "react-i18next";
import socket from "../../../services/socket";

const Calls = () => {
  const { t } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchCalls = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await getCallsByRestaurant();
      setCalls(response.calls || []);
      const pending = response.pending || 0;
      setPendingCount(pending);
      
      // Notify SideBar to update badge count
      window.dispatchEvent(new CustomEvent("calls-count-updated", { detail: { pending } }));
      console.log(`📊 Calls count updated: ${pending} pending`);
    } catch (error) {
      console.error("Error fetching calls:", error);
      if (showLoading) {
        toast.error(error.message || t("Failed to fetch calls") || "Failed to fetch calls");
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [t]);

  useEffect(() => {
    // Setup socket connection and join restaurant room
    const setupSocket = async () => {
      try {
        // Wait for socket to be connected
        if (!socket.connected) {
          socket.connect();
          await new Promise((resolve) => {
            if (socket.connected) {
              resolve();
            } else {
              socket.once("connect", resolve);
            }
          });
        }

        const user = await fetchConnectedUser();
        const restaurantId = user.restaurantFK;
        if (restaurantId) {
          // Ensure restaurantId is a string
          const restaurantIdStr = restaurantId.toString();
          socket.emit("join-restaurant", restaurantIdStr);
          console.log(`✅ Joined restaurant room: ${restaurantIdStr}`);
        }
      } catch (error) {
        console.error("Error setting up socket:", error);
      }
    };

    // Initial fetch with loading indicator
    fetchCalls(true);
    setupSocket();

    // Listen for new server calls in real-time - add new call directly to state
    const handleServerCall = (data) => {
      console.log("📞 New server call received in Calls component:", data);
      
      // Create new call object from socket data
      const newCall = {
        _id: data.callId,
        restaurantId: data.restaurantId,
        tableNumber: data.tableNumber,
        status: "pending",
        createdAt: new Date().toISOString(),
      };

      console.log("📝 Adding new call to table:", newCall);

      // Add new call to the beginning of the list (most recent first)
      setCalls((prevCalls) => {
        // Check if call already exists to avoid duplicates
        const exists = prevCalls.some((call) => call._id === newCall._id);
        if (exists) {
          console.log("⚠️ Call already exists, skipping duplicate");
          return prevCalls;
        }
        console.log(`✅ Adding new call. Previous count: ${prevCalls.length}, New count: ${prevCalls.length + 1}`);
        return [newCall, ...prevCalls];
      });

      // Update pending count
      setPendingCount((prev) => {
        const newCount = prev + 1;
        console.log(`📊 Pending count updated: ${prev} → ${newCount}`);
        // Notify SideBar to update badge count
        window.dispatchEvent(new CustomEvent("calls-count-updated", { detail: { pending: newCount } }));
        return newCount;
      });
    };

    // Listen for socket connection events
    const handleConnect = () => {
      console.log("✅ Socket connected in Calls component");
    };

    const handleDisconnect = () => {
      console.log("❌ Socket disconnected in Calls component");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("server-call", handleServerCall);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("server-call", handleServerCall);
    };
  }, [fetchCalls]);

  const handleServerOnTheWay = async (call) => {
    try {
      // Optimistically update the call status in the UI
      setCalls((prevCalls) =>
        prevCalls.map((c) =>
          c._id === call._id ? { ...c, status: "acknowledged" } : c
        )
      );
      setPendingCount((prev) => {
        const newCount = Math.max(0, prev - 1);
        // Notify SideBar to update badge count
        window.dispatchEvent(new CustomEvent("calls-count-updated", { detail: { pending: newCount } }));
        return newCount;
      });

      // Acknowledge the call (updates status in database)
      const updatedCall = await acknowledgeCall(call._id);

      // Send response to client via socket
      // Ensure restaurantId is a string
      const restaurantIdStr = call.restaurantId?.toString ? call.restaurantId.toString() : String(call.restaurantId);
      const tableNumberStr = call.tableNumber?.toString ? call.tableNumber.toString() : String(call.tableNumber);
      
      console.log("📤 Sending server-response:", {
        callId: call._id,
        tableNumber: tableNumberStr,
        restaurantId: restaurantIdStr,
        response: "on-the-way",
      });
      
      socket.emit("server-response", {
        callId: call._id,
        tableNumber: tableNumberStr,
        restaurantId: restaurantIdStr,
        response: "on-the-way",
      });

      toast.success(
        t("Notification sent to table") || `Notification sent to table ${call.tableNumber}`
      );
    } catch (error) {
      console.error("Error acknowledging call:", error);
      // Revert optimistic update on error - restore original status
      setCalls((prevCalls) =>
        prevCalls.map((c) =>
          c._id === call._id ? { ...c, status: "pending" } : c
        )
      );
      setPendingCount((prev) => {
        const newCount = prev + 1;
        // Notify SideBar to update badge count
        window.dispatchEvent(new CustomEvent("calls-count-updated", { detail: { pending: newCount } }));
        return newCount;
      });
      toast.error(error.message || t("Failed to send notification") || "Failed to send notification");
    }
  };

  const getStatusBadge = (status) => {
    if (status === "pending") {
      return (
        <Badge bg="warning" style={{ fontSize: "12px", padding: "6px 12px" }}>
          {t("Pending") || "Pending"}
        </Badge>
      );
    }
    if (status === "acknowledged") {
      return (
        <Badge bg="info" style={{ fontSize: "12px", padding: "6px 12px" }}>
          {t("Acknowledged") || "Acknowledged"}
        </Badge>
      );
    }
    return (
      <Badge bg="success" style={{ fontSize: "12px", padding: "6px 12px" }}>
        {t("Completed") || "Completed"}
      </Badge>
    );
  };

  return (
    <>
      <PageTitle activeMenu={t("Calls") || "Calls"} motherMenu={t("history")} />
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">{t("Server Calls") || "Server Calls"}</h4>
              {pendingCount > 0 && (
                <Badge bg="warning" style={{ fontSize: "14px", padding: "8px 12px" }}>
                  {pendingCount} {t("pending") || "pending"}
                </Badge>
              )}
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <p>{t("Loading...") || "Loading..."}</p>
                </div>
              ) : calls.length === 0 ? (
                <div className="text-center py-5">
                  <p>{t("No calls found") || "No calls found"}</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-responsive-md">
                    <thead>
                      <tr>
                        <th style={{ padding: "12px", fontWeight: "600", color: "#495057", borderBottom: "2px solid #dee2e6" }}>
                          {t("Table Number") || "Table Number"}
                        </th>
                        <th style={{ padding: "12px", fontWeight: "600", color: "#495057", borderBottom: "2px solid #dee2e6" }}>
                          {t("Customer Name") || "Customer Name"}
                        </th>
                        <th style={{ padding: "12px", fontWeight: "600", color: "#495057", borderBottom: "2px solid #dee2e6" }}>
                          {t("Date") || "Date"}
                        </th>
                        <th style={{ padding: "12px", fontWeight: "600", color: "#495057", borderBottom: "2px solid #dee2e6" }}>
                          {t("Status") || "Status"}
                        </th>
                        <th style={{ padding: "12px", fontWeight: "600", color: "#495057", borderBottom: "2px solid #dee2e6" }}>
                          {t("Actions") || "Actions"}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((call) => (
                        <tr key={call._id}>
                          <td style={{ padding: "12px", verticalAlign: "middle" }}>
                            <strong style={{ fontSize: "16px", color: "#333" }}>
                              {t("Table") || "Table"} {call.tableNumber || "N/A"}
                            </strong>
                          </td>
                          <td style={{ padding: "12px", verticalAlign: "middle" }}>
                            <span style={{ fontSize: "14px", color: "#495057" }}>
                              {call.callerName || "-"}
                            </span>
                          </td>
                          <td style={{ padding: "12px", verticalAlign: "middle" }}>
                            {new Date(call.createdAt).toLocaleString()}
                          </td>
                          <td style={{ padding: "12px", verticalAlign: "middle" }}>
                            {getStatusBadge(call.status)}
                          </td>
                          <td style={{ padding: "12px", verticalAlign: "middle" }}>
                            {call.status === "pending" && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleServerOnTheWay(call)}
                                style={{
                                  backgroundColor: Colors.primary,
                                  borderColor: Colors.primary,
                                  fontSize: "13px",
                                  padding: "6px 16px",
                                }}
                              >
                                {t("Server On The Way") || "Server On The Way"}
                              </Button>
                            )}
                            {call.status === "acknowledged" && (
                              <span style={{ color: "#6c757d", fontSize: "13px" }}>
                                {t("Notified") || "Notified"}
                              </span>
                            )}
                            {call.status === "completed" && (
                              <span style={{ color: "#28a745", fontSize: "13px" }}>
                                {t("Completed") || "Completed"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Calls;

